// --- Framework
import React from 'react';
import PropTypes from 'prop-types';

// --- External tools
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';

// --- IO
import API from 'io/API';

// --- Logic
import TranslationKey from 'logic/enums/TranslationKey';
import { isStringNullOrEmpty, isValidEmailAddress, isValidPhoneNumber } from 'logic/stringOperations';

// --- External components
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';

// --- Component
import PhoneNumberInput from 'visual/components/_/form/PhoneNumberInput';

// --- Style
const styles = theme => ({
	form: {},
});


const formRules = Object.freeze({
	email: [
		value => !!value || { i18nKey: TranslationKey.validation_email_empty },
		value => isValidEmailAddress(value) || { i18nKey: TranslationKey.validation_email_invalid },
	],
	fullname: [
		value => !!value || { i18nKey: TranslationKey.validation_fullname_empty },
	],
	// username: [
	// 	value => !!value || { i18nKey: TranslationKey.validation_username_empty },
	// ],
	password: [
		value => !!value || { i18nKey: TranslationKey.validation_password_empty },
		value => value.length >= 4 || { i18nKey: TranslationKey.validation_string_too_short, values: { min_length: 4 } },
	],
	phone: [
		value => (isStringNullOrEmpty(value) || isValidPhoneNumber(value)) || { i18nKey: TranslationKey.validation_phone_invalid },
	],
});

const defaultState = Object.freeze({
	email: '',
	fullname: '',
	username: '',
	password: '',
	phone: '',
	errors: {
		email: [],
		fullname: [],
		// username: [],
		password: [],
		phone: [],
	},
	requestError: null,
	isFormDisabled: false,
	infoMessage: null,
});

class Register extends React.Component {
	constructor(props) {
		super(props);

		this.state = { ...defaultState };

		this.onSubmit = this.onSubmit.bind(this);
		this.validateForm = this.validateForm.bind(this);
		this.switchToSignIn = this.switchToSignIn.bind(this);
	}

	async validateForm() {
		let hasErrors = false;

		await this.setState((currentState) => {
			const errors = Object.keys(formRules).reduce((result, field) => {
				result[field] = formRules[field]
					.map(rule => rule(currentState[field]))
					.filter(ruleResult => ruleResult !== true);

				if (result[field].length > 0)
					hasErrors = true;

				return result;
			}, {});

			console.log('Form errors', errors);

			return { errors };
		});

		return hasErrors;
	}

	async onSubmit(event) {
		if (event != null)
			event.preventDefault();

		// Immediately show verification message based on selected language
		const lang = (this.props.i18n && this.props.i18n.language ? this.props.i18n.language : 'en').toLowerCase();
		const isDutch = lang.startsWith('nl');
		const immediateMessage = isDutch
			? 'De verificatiemail is naar uw e-mailadres verzonden. Controleer uw inbox en spamfolder.'
			: 'Verification email has been sent to your e-mail address. Please check your inbox and spam folder.';

		await this.setState(currentState => ({
			requestError: null,
			infoMessage: { i18nKey: 'immediate_verification_notice', defaultValue: immediateMessage },
			isFormDisabled: true,
			email: currentState.email.toLowerCase(),
		}));

		const {
			props: {
				onSignedIn,
				getPreferencesForNewUser,
			},
			state: {
				email,
				fullname,
				username,
				password,
				phone,
			}
		} = this;

		const hasErrors = await this.validateForm();

		// Invalid fields.
		if (hasErrors) {
			await this.setState({ isFormDisabled: false, infoMessage: null });
			return;
		}

		const body = {
			...getPreferencesForNewUser(),
			email,
			password,
			users: username,
			voor_en_achternaam: fullname,
		};

		if (!isStringNullOrEmpty(phone))
			body.telefoon = phone;

		try {
			const response = await API.signUpUser(body);
			console.log('Register response', response);

			const responseMessage = response?.data?.message;
			if (response.status === 201 && typeof responseMessage === 'string' && responseMessage.toLowerCase() === 'verification email sent') {
				await this.setState({ isFormDisabled: false });
				return;
			}

			// Do not auto sign-in here. Keep the immediate message; just re-enable the form.
			await this.setState({ isFormDisabled: false });
		} catch (error) {
			console.error('Registration error:', error);
			let errorMessage = TranslationKey.error_register_unknown_reason;
			
				if (error.response && error.response.data && error.response.data.error)
					errorMessage = error.response.data.error;
				else if (error.message)
					errorMessage = error.message;

			await this.setState({
				isFormDisabled: false,
				requestError: { message: errorMessage },
				infoMessage: null,
			});
		}
	}

	switchToSignIn() {
		const { history } = this.props;

		history.push('/sign-in');
	}

	render() {
		const {
			props: {
				t,
				classes,
			},
			state: {
				errors,
				requestError,
				isFormDisabled,
				infoMessage,
			}
		} = this;

		/*
		<Grid item>
			<TextField
				required
				fullWidth
				size="small"
				autoComplete="off"
				variant="outlined"
				disabled={isFormDisabled}
				value={this.state.username}
				label={t(TranslationKey.username)}
				error={errors.username.length > 0}
				helperText={errors.username.length > 0 ? t(errors.username[0].i18nKey, errors.username[0].values) : null}
				onChange={({ target: { value } }) => this.setState((previousState) => {
					if (errors.username.length > 0)
						return { username: value, errors: { ...previousState.errors, username: [] } };
					return { username: value };
				})}
			/>
		</Grid>

		<TextField
			fullWidth
			size="small"
			type="phone"
			variant="outlined"
			autoComplete="off"
			disabled={isFormDisabled}
			value={this.state.phone}
			label={t(TranslationKey.phone)}
			error={errors.phone.length > 0}
			// FormHelperTextProps={{ style: { textAlign: 'justify' } }}
			helperText={errors.phone.length > 0 ? t(errors.phone[0].i18nKey, errors.phone[0].values) : t(TranslationKey.help_text_enter_phone_number)}
			onChange={({ target: { value } }) => this.setState((previousState) => {
				if (errors.phone.length > 0)
					return { phone: value, errors: { ...previousState.errors, phone: [] } };
				return { phone: value };
			})}
		/>
		*/

		return (
			<Container
				disableGutters
				className="page"
			>
				<Typography
					variant="h5"
					component="h2"
					className="pageTitle"
					style={{ textAlign: 'center', marginBottom: '16px' }}
				>
					{t(TranslationKey.register)}
				</Typography>
				<Container
					noValidate
					maxWidth="xs"
					disableGutters
					component="form"
					onSubmit={this.onSubmit}
					className="pageContainer"
				>
					<Grid
						container
						spacing={2}
						justify="center"
						direction="column"
						className="content"
						alignItems="stretch"
					>
						<Grid item>
							<TextField
								required
								fullWidth
								type="email"
								size="small"
								variant="outlined"
								disabled={isFormDisabled}
								value={this.state.email}
								label={t(TranslationKey.email)}
								error={errors.email.length > 0}
								helperText={errors.email.length > 0 ? t(errors.email[0].i18nKey, errors.email[0].values) : null}
								onChange={({ target: { value } }) => this.setState((previousState) => {
									if (errors.email.length > 0)
										return { email: value, username: value, errors: { ...previousState.errors, email: [] } };
									return { email: value, username: value };
								})}
							/>
						</Grid>
						<Grid item>
							<TextField
								required
								fullWidth
								size="small"
								variant="outlined"
								disabled={isFormDisabled}
								value={this.state.fullname}
								label={t(TranslationKey.fullname)}
								error={errors.fullname.length > 0}
								helperText={errors.fullname.length > 0 ? t(errors.fullname[0].i18nKey, errors.fullname[0].values) : null}
								onChange={({ target: { value } }) => this.setState((previousState) => {
									if (errors.fullname.length > 0)
										return { fullname: value, errors: { ...previousState.errors, fullname: [] } };
									return { fullname: value };
								})}
							/>
						</Grid>
						<Grid item>
							<PhoneNumberInput
								country={'nl'}
								value={this.state.phone}
								label={t(TranslationKey.phone)}
								error={errors.phone.length > 0}
								id="register-phone-number-input-field"
								helperText={errors.phone.length > 0 ? t(errors.phone[0].i18nKey, errors.phone[0].values) : t(TranslationKey.help_text_enter_phone_number)}
								onChange={value => this.setState((previousState) => {
									if (errors.phone.length > 0)
										return { phone: value, errors: { ...previousState.errors, phone: [] } };
									return { phone: value };
								})}
							/>
						</Grid>
						<Grid item>
							<TextField
								required
								fullWidth
								size="small"
								type="password"
								id="new-password"
								variant="outlined"
								disabled={isFormDisabled}
								autoComplete="new-password"
								value={this.state.password}
								label={t(TranslationKey.password)}
								error={errors.password.length > 0}
								helperText={errors.password.length > 0 ? t(errors.password[0].i18nKey, errors.password[0].values) : null}
									onChange={({ target: { value } }) => this.setState((previousState) => {
										if (errors.password.length > 0)
											return { password: value, errors: { ...previousState.errors, password: [] } };
										return { password: value };
									})}
							/>
						</Grid>

						{requestError && (
							<Grid item>
								<Typography color="error" variant="body2">
									{requestError.message || t(requestError.i18nKey, requestError.values)}
								</Typography>
							</Grid>
						)}
						{infoMessage && (
							<Grid item>
								<Typography color="primary" variant="body2">
									{t(infoMessage.i18nKey, { defaultValue: infoMessage.defaultValue })}
								</Typography>
							</Grid>
						)}

						<Grid item>
							<Button
								fullWidth
								size="large"
								type="submit"
								color="primary"
								disableElevation
								variant="contained"
								disabled={isFormDisabled}
							>
								{t(TranslationKey.register)}
							</Button>
						</Grid>
						<Grid item>
							<Button
								fullWidth
								variant="text"
								color="primary"
								disabled={isFormDisabled}
								onClick={this.switchToSignIn}
							>
								{t(TranslationKey.sign_in)}
							</Button>
						</Grid>
					</Grid>
				</Container>
			</Container>
		);
	}
}

Register.propTypes = {
	t: PropTypes.func,
	i18n: PropTypes.object,
	history: PropTypes.object.isRequired,
	classes: PropTypes.object.isRequired,
	onSignedIn: PropTypes.func.isRequired,
	getPreferencesForNewUser: PropTypes.func.isRequired,
};

export default compose(
	withTranslation(),
	withStyles(styles),
)(Register);
